/**
 * v5-compatible wrapper around @tanstack/svelte-query v6.
 *
 * v6 dropped the store-based Svelte adapter for a runes-based one that binds
 * each observer's subscription to a component `$effect`. This app creates
 * queries inside context factories and consumes them lazily through stores
 * across component boundaries, so an `$effect`-bound observer goes inactive
 * and `invalidateQueries` silently skips it. To preserve v5 semantics we
 * re-implement the create* functions on query-core's observers directly (the
 * same query-core instance v6 re-exports, so the QueryClient/context stays
 * shared with @viamrobotics/svelte-sdk and @viamrobotics/test-widgets), with
 * observer lifetime tied to store subscribers exactly as v5 did.
 *
 * This is the only file in ui allowed to import '@tanstack/svelte-query'
 * directly (enforced by no-restricted-imports).
 */
import { getContext, onDestroy, setContext } from 'svelte';
import { derived, get, readable, type Readable } from 'svelte/store';

// eslint-disable-next-line no-restricted-imports
import {
  InfiniteQueryObserver,
  MutationObserver,
  noop,
  notifyManager,
  QueriesObserver,
  QueryObserver,
  type CreateBaseMutationResult,
  type CreateInfiniteQueryOptions,
  type CreateMutationOptions,
  type CreateQueryOptions,
  type DefaultError,
  type DefinedInitialDataOptions,
  type DefinedQueryObserverResult,
  type InfiniteData,
  type InfiniteQueryObserverResult,
  type MutationObserverResult,
  type QueriesObserverOptions,
  type QueriesOptions,
  type QueriesResults,
  type QueryClient,
  type QueryKey,
  type QueryObserverOptions,
  type QueryObserverResult,
  type UndefinedInitialDataOptions,
} from '@tanstack/svelte-query';

// Unchanged v6 symbols (useQueryClient, setQueryClientContext, queryOptions,
// keepPreviousData, QueryClient, and all query-core exports) pass through;
// the local declarations below shadow the star re-exports.
// eslint-disable-next-line no-restricted-imports
export * from '@tanstack/svelte-query';

/**
 * Allows a type to be either the base object or a store of that object.
 *
 * @deprecated v5 option shape; the native v6 API passes options via an accessor
 * (`() => options`), not a store.
 */
export type StoreOrVal<T> = T | Readable<T>;

/**
 * Result from createQuery, in v5 store form.
 *
 * @deprecated v5 store-shape result type; the native v6 API returns a reactive
 * result, not a `Readable`.
 */
export type CreateBaseQueryResult<
  TData = unknown,
  TError = DefaultError,
> = Readable<QueryObserverResult<TData, TError>>;
/**
 * @deprecated v5 store-shape result type; the native v6 API returns a reactive
 * result, not a `Readable`.
 */
export type CreateQueryResult<
  TData = unknown,
  TError = DefaultError,
> = CreateBaseQueryResult<TData, TError>;

/**
 * Result from createQuery with initialData, in v5 store form.
 *
 * @deprecated v5 store-shape result type; the native v6 API returns a reactive
 * result, not a `Readable`.
 */
export type DefinedCreateBaseQueryResult<
  TData = unknown,
  TError = DefaultError,
> = Readable<DefinedQueryObserverResult<TData, TError>>;
/**
 * @deprecated v5 store-shape result type; the native v6 API returns a reactive
 * result, not a `Readable`.
 */
export type DefinedCreateQueryResult<
  TData = unknown,
  TError = DefaultError,
> = DefinedCreateBaseQueryResult<TData, TError>;

/**
 * Result from createInfiniteQuery, in v5 store form.
 *
 * @deprecated v5 store-shape result type; the native v6 API returns a reactive
 * result, not a `Readable`.
 */
export type CreateInfiniteQueryResult<
  TData = unknown,
  TError = DefaultError,
> = Readable<InfiniteQueryObserverResult<TData, TError>>;

/**
 * Result from createMutation, in v5 store form.
 *
 * @deprecated v5 store-shape result type; the native v6 API returns a reactive
 * result, not a `Readable`.
 */
export type CreateMutationResult<
  TData = unknown,
  TError = DefaultError,
  TVariables = unknown,
  TContext = unknown,
> = Readable<CreateBaseMutationResult<TData, TError, TVariables, TContext>>;

const isStore = <T>(value: StoreOrVal<T>): value is Readable<T> =>
  typeof value === 'object' &&
  value !== null &&
  'subscribe' in value &&
  typeof value.subscribe === 'function';

/**
 * The app keeps its QueryClient under its own context key, separate from the
 * key `@viamrobotics/svelte-sdk` uses. Both packages resolve to the single
 * deduped v6 module and therefore share v6's default Symbol context key, so
 * without this the SDK's `<ViamProvider>` (which sets its own QueryClient on
 * that key) shadows the app's client for any app code rendered inside it, e.g.
 * the config builder's mutations would invalidate the SDK's client instead of
 * the app's, silently dropping refetches. v5 avoided this because its context
 * key differed from v6's..
 */
const QUERY_CLIENT_CONTEXT_KEY = Symbol('viam-app-query-client');

export const setQueryClientContext = (client: QueryClient): void => {
  setContext(QUERY_CLIENT_CONTEXT_KEY, client);
};

export const getQueryClientContext = (): QueryClient => {
  const client = getContext<QueryClient | undefined>(QUERY_CLIENT_CONTEXT_KEY);
  if (!client) {
    throw new Error(
      'No QueryClient was found in Svelte context. Did you forget to call provideAPIClients?'
    );
  }
  return client;
};

// eslint-disable-next-line func-style
export function useQueryClient(queryClient?: QueryClient): QueryClient {
  return queryClient ?? getQueryClientContext();
}

// query-core observers share a base shape; typed loosely here because the
// public create* signatures below carry the precise generics for callers.
interface BaseObserver {
  setOptions: (options: QueryObserverOptions) => void;
  subscribe: (listener: (result: unknown) => void) => () => void;
  updateResult: () => void;
  getOptimisticResult: (options: QueryObserverOptions) => unknown;
  trackResult: (result: unknown) => unknown;
}
type ObserverConstructor = new (
  client: QueryClient,
  options: QueryObserverOptions
) => BaseObserver;

/**
 * v5's `createBaseQuery`: the observer subscription lives inside the readable's
 * start/stop, so it is active exactly while the returned store has subscribers.
 */
const createBaseQuery = <TResult>(
  options: StoreOrVal<QueryObserverOptions>,
  Observer: ObserverConstructor,
  queryClient?: QueryClient
): Readable<TResult> => {
  const client = useQueryClient(queryClient);
  const optionsStore = isStore(options) ? options : readable(options);

  const defaultedOptionsStore = derived(optionsStore, ($options) => {
    const defaulted = client.defaultQueryOptions($options);
    // eslint-disable-next-line no-underscore-dangle
    defaulted._optimisticResults = 'optimistic';
    return defaulted;
  });

  const observer = new Observer(client, get(defaultedOptionsStore));
  onDestroy(
    defaultedOptionsStore.subscribe(($defaulted) => {
      observer.setOptions($defaulted);
    })
  );

  const result = readable(
    observer.getOptimisticResult(get(defaultedOptionsStore)),
    (set) => {
      const unsubscribe = observer.subscribe(notifyManager.batchCalls(set));
      observer.updateResult();
      return unsubscribe;
    }
  );

  return derived([result, defaultedOptionsStore], ([, $defaulted]) => {
    const next = observer.getOptimisticResult($defaulted);
    return (
      $defaulted.notifyOnChangeProps ? next : observer.trackResult(next)
    ) as TResult;
  });
};

/**
 * @deprecated Prefer the native @tanstack/svelte-query v6 API for new queries;
 * this v5 store-compatible wrapper remains for existing consumers only. Resolve
 * the app's client via `useQueryClient()` from this module.
 */
export function createQuery<
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options:
    | StoreOrVal<
        UndefinedInitialDataOptions<TQueryFnData, TError, TData, TQueryKey>
      >
    | StoreOrVal<CreateQueryOptions<TQueryFnData, TError, TData, TQueryKey>>,
  queryClient?: QueryClient
): CreateQueryResult<TData, TError>;

/**
 * @deprecated Prefer the native @tanstack/svelte-query v6 API for new queries;
 * this v5 store-compatible wrapper remains for existing consumers only. Resolve
 * the app's client via `useQueryClient()` from this module.
 */
export function createQuery<
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: StoreOrVal<
    DefinedInitialDataOptions<TQueryFnData, TError, TData, TQueryKey>
  >,
  queryClient?: QueryClient
): DefinedCreateQueryResult<TData, TError>;

// eslint-disable-next-line func-style
export function createQuery(
  options: StoreOrVal<CreateQueryOptions>,
  queryClient?: QueryClient
): CreateQueryResult {
  return createBaseQuery(
    options as StoreOrVal<QueryObserverOptions>,
    QueryObserver as unknown as ObserverConstructor,
    queryClient
  );
}

/**
 * @deprecated Prefer the native @tanstack/svelte-query v6 API for new queries;
 * this v5 store-compatible wrapper remains for existing consumers only. Resolve
 * the app's client via `useQueryClient()` from this module.
 */
export const createInfiniteQuery = <
  TQueryFnData,
  TError = DefaultError,
  TData = InfiniteData<TQueryFnData>,
  TQueryKey extends QueryKey = QueryKey,
  TPageParam = unknown,
>(
  options: StoreOrVal<
    CreateInfiniteQueryOptions<
      TQueryFnData,
      TError,
      TData,
      TQueryKey,
      TPageParam
    >
  >,
  queryClient?: QueryClient
): CreateInfiniteQueryResult<TData, TError> =>
  createBaseQuery(
    options as unknown as StoreOrVal<QueryObserverOptions>,
    InfiniteQueryObserver as unknown as ObserverConstructor,
    queryClient
  );

/**
 * @deprecated Prefer the native @tanstack/svelte-query v6 API for new
 * mutations; this v5 store-compatible wrapper remains for existing consumers
 * only. Resolve the app's client via `useQueryClient()` from this module.
 */
export const createMutation = <
  TData = unknown,
  TError = DefaultError,
  TVariables = void,
  TContext = unknown,
>(
  options: StoreOrVal<
    CreateMutationOptions<TData, TError, TVariables, TContext>
  >,
  queryClient?: QueryClient
): CreateMutationResult<TData, TError, TVariables, TContext> => {
  const client = useQueryClient(queryClient);
  const optionsStore = isStore(options) ? options : readable(options);

  const observer = new MutationObserver<TData, TError, TVariables, TContext>(
    client,
    get(optionsStore)
  );

  const mutate: CreateBaseMutationResult<
    TData,
    TError,
    TVariables,
    TContext
  >['mutate'] = (variables, mutateOptions) => {
    observer.mutate(variables, mutateOptions).catch(noop);
  };
  onDestroy(
    optionsStore.subscribe(($options) => {
      observer.setOptions($options);
    })
  );

  const result = readable(observer.getCurrentResult(), (set) =>
    observer.subscribe(notifyManager.batchCalls(set))
  );

  return derived(
    result,
    ($result: MutationObserverResult<TData, TError, TVariables, TContext>) => ({
      ...$result,
      mutate,
      mutateAsync: $result.mutate,
    })
  );
};

/**
 * @deprecated Prefer the native @tanstack/svelte-query v6 API for new queries;
 * this v5 store-compatible wrapper remains for existing consumers only. Resolve
 * the app's client via `useQueryClient()` from this module.
 */
export const createQueries = <
  T extends unknown[],
  TCombinedResult = QueriesResults<T>,
>(
  {
    queries,
    combine,
  }: {
    queries: StoreOrVal<readonly [...QueriesOptions<T>]>;
    combine?: (result: QueriesResults<T>) => TCombinedResult;
  },
  queryClient?: QueryClient
): Readable<TCombinedResult> => {
  const client = useQueryClient(queryClient);
  const queriesStore = isStore(queries) ? queries : readable(queries);
  const defaultedQueriesStore = derived(
    queriesStore,
    ($queries) =>
      $queries.map((opts) => {
        const defaultedOptions = client.defaultQueryOptions(
          opts as Parameters<QueryClient['defaultQueryOptions']>[0]
        );
        // eslint-disable-next-line no-underscore-dangle
        defaultedOptions._optimisticResults = 'optimistic';
        return defaultedOptions;
      }) as QueryObserverOptions<unknown, Error, unknown, unknown>[]
  );

  const observer = new QueriesObserver<TCombinedResult>(
    client,
    get(defaultedQueriesStore),
    { combine } as QueriesObserverOptions<TCombinedResult>
  );
  onDestroy(
    defaultedQueriesStore.subscribe(($defaultedQueries) => {
      // Options changes don't notify; they're reflected in the optimistic
      // result computed below.
      observer.setQueries($defaultedQueries, {
        combine,
      } as QueriesObserverOptions<TCombinedResult>);
    })
  );

  const subscribedResult = readable(observer.getCurrentResult(), (set) =>
    observer.subscribe(notifyManager.batchCalls(set))
  );

  return derived(
    [subscribedResult, defaultedQueriesStore],
    ([, $defaultedQueries]) => {
      const [, getCombinedResult, trackResult] = observer.getOptimisticResult(
        $defaultedQueries,
        combine as QueriesObserverOptions<TCombinedResult>['combine']
      );
      return getCombinedResult(trackResult());
    }
  );
};
